import { useState, useEffect, useRef } from "react";
import { MessageCircle, Circle, X, Send } from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

const WS_URL = BACKEND_URL.replace("https://", "wss://").replace("http://", "ws://");

const AdminChatTab = ({ authToken }) => {
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const endRef = useRef(null);
  const headers = { 'Content-Type': 'application/json', ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) };

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const fetchSessions = async () => {
    try { const res = await fetch(`${BACKEND_URL}/api/chat/admin/sessions`, { headers }); if (res.ok) { const d = await res.json(); setSessions(d.sessions || []); } } catch (e) { console.error(e); }
  };

  const connectWs = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    const ws = new WebSocket(`${WS_URL}/api/chat/ws/admin`);
    ws.onopen = () => setConnected(true);
    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data);
      if (msg.type === 'new_message') {
        setMessages(prev => [...prev, msg]);
        setSessions(prev => prev.map(s => s.session_id === msg.session_id ? { ...s, last_message: msg.message, last_message_at: msg.timestamp, unread: msg.sender_type === 'user' } : s));
      }
    };
    ws.onclose = () => { setConnected(false); setTimeout(connectWs, 3000); };
    wsRef.current = ws;
  };

  useEffect(() => { fetchSessions(); connectWs(); return () => { wsRef.current?.close(); }; }, []);

  const openSession = async (session) => {
    setActive(session);
    try { const res = await fetch(`${BACKEND_URL}/api/chat/messages/${session.session_id}`, { headers }); if (res.ok) { const d = await res.json(); setMessages(d.messages || []); } } catch (e) { console.error(e); }
    if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.send(JSON.stringify({ type: 'read', session_id: session.session_id }));
    setSessions(prev => prev.map(s => s.session_id === session.session_id ? { ...s, unread: false } : s));
  };

  const send = () => {
    if (!reply.trim() || !active || wsRef.current?.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ type: 'message', session_id: active.session_id, message: reply.trim() }));
    setReply('');
  };

  const closeSession = async (sessionId) => {
    try { await fetch(`${BACKEND_URL}/api/chat/admin/session/${sessionId}`, { method: 'DELETE', headers }); setSessions(prev => prev.filter(s => s.session_id !== sessionId)); if (active?.session_id === sessionId) { setActive(null); setMessages([]); } } catch (e) { console.error(e); }
  };

  return (
    <div className="flex h-[calc(100vh-240px)] gap-4" data-testid="admin-livechat-tab">
      <div className="w-80 flex-shrink-0 bg-white/5 border border-white/10 rounded-2xl overflow-hidden flex flex-col">
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2"><h3 className="text-white font-semibold text-sm">Conversations</h3><Circle className={`w-2.5 h-2.5 fill-current ${connected ? 'text-green-400' : 'text-red-400'}`} /></div>
          <span className="text-white/50 text-xs">{sessions.filter(s => s.status === 'active').length} active</span>
        </div>
        <div className="flex-1 overflow-y-auto divide-y divide-white/5">
          {sessions.filter(s => s.status === 'active').length === 0 ? (
            <div className="p-8 text-center"><MessageCircle className="w-10 h-10 text-white/15 mx-auto mb-3" /><p className="text-white/40 text-sm">No active chats</p></div>
          ) : sessions.filter(s => s.status === 'active').map(s => (
            <button key={s.session_id} onClick={() => openSession(s)} className={`w-full text-left p-3 hover:bg-white/5 transition ${active?.session_id === s.session_id ? 'bg-purple-600/20 border-l-2 border-purple-500' : ''}`} data-testid={`chat-session-${s.session_id}`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">{s.unread && <span className="w-2 h-2 rounded-full bg-purple-400 flex-shrink-0" />}<p className={`text-sm truncate ${s.unread ? 'text-white font-semibold' : 'text-white/80'}`}>{s.user_name || 'Visitor'}</p></div>
                  {s.user_email && <p className="text-white/40 text-xs truncate">{s.user_email}</p>}
                  <p className="text-white/30 text-xs truncate mt-1">{s.last_message || 'No messages yet'}</p>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-2">
                  <span className="text-white/30 text-[10px]">{s.last_message_at ? new Date(s.last_message_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                  <button onClick={(e) => { e.stopPropagation(); closeSession(s.session_id); }} className="text-white/20 hover:text-red-400 transition" title="Close chat"><X className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 bg-white/5 border border-white/10 rounded-2xl overflow-hidden flex flex-col">
        {!active ? (
          <div className="flex-1 flex items-center justify-center text-center"><div><MessageCircle className="w-16 h-16 text-white/10 mx-auto mb-4" /><p className="text-white/40 text-lg font-medium">Select a conversation</p></div></div>
        ) : (
          <>
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <div><p className="text-white font-semibold text-sm">{active.user_name || 'Visitor'}</p><p className="text-white/40 text-xs">{active.user_email || 'No email'}</p></div>
              <div className="flex items-center gap-2"><span className="text-white/30 text-xs">Started {new Date(active.started_at).toLocaleString()}</span><button onClick={() => closeSession(active.session_id)} className="text-white/40 hover:text-red-400 transition text-xs bg-white/5 px-2 py-1 rounded">Close Chat</button></div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
              {messages.filter(m => m.session_id === active.session_id).map((msg, i) => (
                <div key={msg.message_id || i} className={`flex ${msg.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[70%] px-3 py-2 rounded-xl text-sm ${msg.sender_type === 'admin' ? 'bg-purple-600 text-white rounded-br-sm' : 'bg-gray-700 text-white rounded-bl-sm'}`}>
                    {msg.sender_type !== 'admin' && <p className="text-purple-300 text-xs font-medium mb-0.5">{msg.sender_name || 'Visitor'}</p>}
                    <p className="break-words">{msg.message}</p>
                    <p className="text-[10px] opacity-50 mt-0.5">{new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>
            <div className="border-t border-white/10 px-4 py-3 flex gap-2">
              <input type="text" placeholder="Type your reply..." value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && send()}
                className="flex-1 bg-gray-800 border border-gray-600 rounded-full px-4 py-2 text-white text-sm placeholder-gray-500 focus:border-purple-500 focus:outline-none" data-testid="admin-chat-reply-input" />
              <button onClick={send} disabled={!reply.trim()} className="bg-purple-600 hover:bg-purple-700 disabled:opacity-40 text-white px-4 py-2 rounded-full transition flex items-center gap-2 text-sm" data-testid="admin-chat-send-btn"><Send className="w-4 h-4" /> Send</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminChatTab;
